import React from 'react';
import styled, { css } from 'styled-components';
import Link from 'components/core/link';

import HoverImageGallery from '../components/hover-image-gallery';
import { useCardContext } from '../card-context';
import { CardSponsoredTag } from '../card-sponsored-tag';
import { CardName } from '../card-name';
import { CardBrand } from '../card-brand';
import { CardRating } from '../card-rating';
import { CardStrainDetails } from '../card-strain-details';
import { CardTags } from '../card-tags';
import { OptionTileList } from '../list-primitives/option-tile-list';
import { TileLabel } from '../list-primitives/tile-label';
import { TilePrice } from '../list-primitives/tile-price';
import { TileDiscountedPrice } from '../list-primitives/tile-discounted-price';
import { TileAddIcon } from '../list-primitives/tile-add-icon';

type FullCardViewProps = {
  hideBorder?: boolean;
  containerRef: React.Ref<HTMLDivElement>;
  productIndex: number;
};

/**
 * Presentational list card component.
 * Reads product data from CardContext and renders shared primitives.
 */
export const FullCardView = ({ hideBorder, containerRef, productIndex }: FullCardViewProps): JSX.Element => {
  const { image, productName, brandName, href, onLinkClick } = useCardContext();

  return (
    <Wrapper ref={containerRef} $hideBorder={hideBorder} data-placement-index={productIndex}>
      <Link href={href} onClick={onLinkClick}>
        <StyledAnchor>
          <ImageWrapper>
            <HoverImageGallery {...image} alt={`Image of ${productName} product`} height={104} width={104} />
          </ImageWrapper>

          <NameWrapper>
            <CardSponsoredTag desktopMarginBottom={4} />
            <CardName responsive />
            {brandName && <CardBrand marginTop={6} desktopMarginTop={4} />}
            <CardRating />
            <CardStrainDetails gap={8} marginTop={12} />
            <CardTags showStaffPick horizontal marginTop={12} />
          </NameWrapper>
        </StyledAnchor>
      </Link>

      <OptionTileList>
        <TileLabel />
        <TilePrice />
        <TileDiscountedPrice />
        <TileAddIcon />
      </OptionTileList>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $hideBorder?: boolean }>`
  border-top: 1px solid #e3e7e9;
  padding: 20px 0;

  ${({ $hideBorder }) =>
    $hideBorder &&
    css`
      border-top: none;
    `}

  @media screen and (min-width: 740px) {
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;
  }
`;

const StyledAnchor = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 20px;

  @media screen and (min-width: 740px) {
    padding-right: 10px;
  }
`;

const ImageWrapper = styled.div`
  flex-shrink: 0;
  height: 92px;
  width: 92px;

  @media screen and (min-width: 740px) {
    height: 104px;
    width: 104px;
  }

  img {
    display: block;
    max-height: 100%;
    max-width: 100%;
    object-fit: cover;
  }
`;

const NameWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;
